// ============================================================================
// ENGLISH TRANSLATIONS - CHAPTER 6: PAUL'S MISSIONS
// ============================================================================
// Levels 66-78 (13 levels, 39 questions)

export const chapter6Translations = {
  name: "Paul's Missions",
  description: "To the ends of the earth...",
  
  spiritualWisdom: {
    principle: "The Gospel is for all nations and must be proclaimed to the whole world",
    scripture: "Go and make disciples of all nations (Matthew 28:19)",
    application: "Every Christian is called to be a witness for Christ wherever God places them"
  },
  
  levels: {
    66: {
      name: "First Missionary Journey",
      challenge: "Set apart for me Barnabas and Saul for the work to which I have called them...",
      easy: {
        question: "Who accompanied Paul on his first journey?",
        options: ["Peter", "Barnabas", "Silas", "Timothy"],
        hint: "The 'son of encouragement'",
        funFact: "👥 Barnabas had welcomed Paul after his conversion - providential partnership!"
      },
      medium: {
        question: "From which city did they depart for their mission?",
        options: [
          "Jerusalem",
          "Antioch of Syria", 
          "Tarsus",
          "Damascus"
        ],
        hint: "The first mixed Jewish-Gentile church",
        funFact: "🚢 Antioch: launching port for evangelizing the Western world!"
      },
      hard: {
        question: "What does this first journey reveal about missionary strategy?",
        options: [
          "One should stay in their region",
          "The Gospel transcends all cultural boundaries",
          "Only Jews can be saved",
          "Mission is optional"
        ],
        hint: "It's about the universality of the message",
        funFact: "🌊 Crossing the Mediterranean = crossing the threshold to worldwide evangelization!"
      }
    },
    
    67: {
      name: "In Cyprus with Elymas",
      challenge: "You son of the devil, full of all deceit and fraud...",
      easy: {
        question: "Which magician opposed Paul in Cyprus?",
        options: ["Simon", "Elymas", "Apollos", "Alexander"],
        hint: "A false prophet who wanted to turn away the proconsul",
        funFact: "🪄 First direct confrontation between the Gospel and magic!"
      },
      medium: {
        question: "What did Paul do to Elymas to punish him?",
        options: [
          "He banished him",
          "He made him temporarily blind",
          "He ignored him",
          "He debated with him"
        ],
        hint: "Temporary blindness to open his spiritual eyes",
        funFact: "👁️ Divine irony: one who blinded souls becomes temporarily blind!"
      },
      hard: {
        question: "How does this episode illustrate the power of the Gospel?",
        options: [
          "Violence is necessary",
          "Divine truth prevails over lies",
          "We must fight all magicians",
          "Paul was vengeful"
        ],
        hint: "It's about divine authority confronting deception",
        funFact: "⚡ The proconsul believes seeing divine power - effective demonstration!"
      }
    },

    68: {
      name: "In Pisidian Antioch",
      challenge: "It was necessary that the word of God be spoken first to you...",
      easy: {
        question: "Where did Paul usually preach first in each city?",
        options: [
          "In the marketplace",
          "In the synagogue",
          "In homes",
          "In the streets"
        ],
        hint: "Where Jews gathered for worship",
        funFact: "🏛️ Synagogue strategy: reach Jews first, then Gentiles - God's order!"
      },
      medium: {
        question: "How did the Jews react to Paul's message about Jesus?",
        options: [
          "They all believed",
          "Many rejected it and opposed him",
          "They were indifferent",
          "They asked for more time"
        ],
        hint: "Opposition from those who should have been most receptive",
        funFact: "😢 Tragic irony: those with the promises reject the Promise-Keeper!"
      },
      hard: {
        question: "What did Paul declare when Jews rejected the Gospel?",
        options: [
          "He gave up preaching",
          "We turn to the Gentiles",
          "He tried harder with Jews",
          "He changed his message"
        ],
        hint: "A pivotal decision opening doors worldwide",
        funFact: "🚪 Door closes to some, opens to all - Gospel for every nation!"
      }
    },

    69: {
      name: "In Iconium and Lystra",
      challenge: "The gods have come down to us in human form!",
      easy: {
        question: "What did the people of Lystra think Paul and Barnabas were?",
        options: [
          "Prophets",
          "Greek gods",
          "Roman officials",
          "Magicians"
        ],
        hint: "They wanted to worship them as deities",
        funFact: "⚡ Healing miracle so powerful they thought gods had descended!"
      },
      medium: {
        question: "Which gods did they think Paul and Barnabas were?",
        options: [
          "Apollo and Zeus",
          "Hermes and Zeus",
          "Mars and Jupiter",
          "Mercury and Venus"
        ],
        hint: "Barnabas the chief speaker, Paul the messenger",
        funFact: "🗣️ Paul did the talking so they called him Hermes, the messenger god!"
      },
      hard: {
        question: "How did Paul and Barnabas respond to being worshipped?",
        options: [
          "They accepted it",
          "They tore their clothes and redirected worship to God",
          "They enjoyed the honor",
          "They used it for advantage"
        ],
        hint: "They refused worship and pointed to the true God",
        funFact: "👕 Tearing clothes = horror at blasphemy - true servants deflect glory to God!"
      }
    },

    70: {
      name: "Stoning at Lystra", 
      challenge: "They stoned Paul and dragged him out of the city, thinking he was dead...",
      easy: {
        question: "What happened to Paul at Lystra?",
        options: [
          "He was imprisoned",
          "He was stoned and left for dead",
          "He was banished",
          "He was honored"
        ],
        hint: "The same crowd that wanted to worship him",
        funFact: "🪨 From worship to attempted murder - fickle human nature!"
      },
      medium: {
        question: "What did Paul do after being stoned?",
        options: [
          "He fled immediately",
          "He got up and went back into the city",
          "He sought medical help",
          "He rested for weeks"
        ],
        hint: "Incredible courage and divine protection",
        funFact: "💪 Supernatural recovery - Paul walks back into the city that stoned him!"
      },
      hard: {
        question: "What does Paul's response teach about perseverance?",
        options: [
          "Avoid dangerous places",
          "Suffering for Christ strengthens rather than defeats us",
          "Revenge is sweet",
          "Mission work is too risky"
        ],
        hint: "It's about invincible commitment to the Gospel",
        funFact: "🔥 'Strengthening the souls of disciples' after being stoned - unstoppable!"
      }
    },

    71: {
      name: "The Jerusalem Council",
      challenge: "It seemed good to the Holy Spirit and to us...",
      easy: {
        question: "What major question did the Jerusalem Council address?",
        options: [
          "Where to preach next",
          "Must Gentiles be circumcised to be Christians",
          "Who should be apostles",
          "What to eat"
        ],
        hint: "The requirements for Gentile conversion",
        funFact: "✂️ Circumcision debate = Gospel freedom vs. legalistic requirements!"
      },
      medium: {
        question: "What was the Council's decision about Gentile Christians?",
        options: [
          "They must become Jews first",
          "They don't need circumcision, just avoid certain practices",
          "They can't be saved",
          "Different rules for different people"
        ],
        hint: "Grace alone, with practical guidelines",
        funFact: "🆓 Historic decision: Gospel by faith alone, not works of law!"
      },
      hard: {
        question: "What principle did this establish for the Church?",
        options: [
          "Different standards for different groups",
          "Unity in essentials, liberty in non-essentials",
          "Jewish customs are required",
          "Anything goes"
        ],
        hint: "It's about distinguishing Gospel essentials from cultural practices",
        funFact: "⚖️ Foundation of Christian liberty - Gospel core vs cultural expressions!"
      }
    },

    72: {
      name: "Second Missionary Journey Begins",
      challenge: "Come over to Macedonia and help us!",
      easy: {
        question: "Who replaced Barnabas as Paul's companion?",
        options: ["Timothy", "Silas", "Luke", "Titus"],
        hint: "Also called Silvanus",
        funFact: "🤝 Silas = new partnership after disagreement with Barnabas!"
      },
      medium: {
        question: "What vision did Paul see at Troas?",
        options: [
          "An angel",
          "A man from Macedonia saying 'Come help us'",
          "Jesus calling him",
          "A great crowd"
        ],
        hint: "A call to cross into Europe",
        funFact: "🌍 Macedonian call = Gospel enters Europe - world-changing vision!"
      },
      hard: {
        question: "What does the Macedonian call teach about guidance?",
        options: [
          "Visions are common",
          "God opens doors to new opportunities through clear leading",
          "We should go everywhere",
          "Only apostles get visions"
        ],
        hint: "It's about divine direction for strategic advancement",
        funFact: "🗺️ One vision changes world history - Gospel reaches Europe!"
      }
    },

    73: {
      name: "Lydia's Conversion",
      challenge: "The Lord opened her heart to respond to Paul's message...",
      easy: {
        question: "What was Lydia's profession?",
        options: [
          "Teacher",
          "Seller of purple cloth",
          "Baker",
          "Innkeeper"
        ],
        hint: "She dealt in expensive luxury goods",
        funFact: "🟣 Purple = luxury business - Lydia was a successful businesswoman!"
      },
      medium: {
        question: "Where did Paul meet Lydia?",
        options: [
          "In a synagogue",
          "By a river where women prayed",
          "In the marketplace",
          "In her home"
        ],
        hint: "A place of prayer outside the city",
        funFact: "🏞️ Riverside prayer meeting becomes Europe's first church!"
      },
      hard: {
        question: "What does Lydia's conversion demonstrate?",
        options: [
          "Only poor people can be saved",
          "The Gospel reaches all social classes and backgrounds",
          "Business people can't be spiritual",
          "Women can't lead in ministry"
        ],
        hint: "It's about the Gospel's universal appeal",
        funFact: "💼 Successful businesswoman becomes first European convert - Gospel for all!"
      }
    },

    74: {
      name: "The Philippian Jailer",
      challenge: "Sirs, what must I do to be saved?",
      easy: {
        question: "Why were Paul and Silas in prison?",
        options: [
          "They stole money",
          "They cast a demon out of a slave girl",
          "They preached illegally",
          "They caused a riot"
        ],
        hint: "They freed someone from spiritual bondage",
        funFact: "👧 Helping a demonized girl led to false accusations and imprisonment!"
      },
      medium: {
        question: "What caused the jailer to ask about salvation?",
        options: [
          "Paul's preaching",
          "An earthquake that freed all prisoners",
          "A vision",
          "Fear of punishment"
        ],
        hint: "A supernatural event that shook more than the building",
        funFact: "🏛️ Earthquake opens prison doors and the jailer's heart!"
      },
      hard: {
        question: "What does this event teach about God's sovereignty?",
        options: [
          "Accidents happen",
          "God can use any circumstance to bring salvation",
          "Earthquakes are judgments",
          "Miracles only happen to apostles"
        ],
        hint: "It's about God working through all circumstances",
        funFact: "⛓️ Prison becomes a place of liberation - physical and spiritual freedom!"
      }
    },

    75: {
      name: "Uproar in Thessalonica",
      challenge: "These men who have turned the world upside down have come here too!",
      easy: {
        question: "How were Paul and his companions described?",
        options: [
          "Peaceful teachers",
          "Men who turned the world upside down",
          "Roman citizens",
          "Jewish rabbis"
        ],
        hint: "World-changers and troublemakers",
        funFact: "🌍 Best compliment ever: turning the world upside down for Christ!"
      },
      medium: {
        question: "What did the Jews accuse Paul of preaching?",
        options: [
          "Roman rebellion",
          "Another king named Jesus",
          "Jewish superiority",
          "Religious reforms"
        ],
        hint: "A rival to Caesar's authority",
        funFact: "👑 Proclaiming King Jesus threatened earthly powers!"
      },
      hard: {
        question: "Why does the Gospel create opposition?",
        options: [
          "It's inherently violent",
          "It challenges existing power structures and worldviews",
          "Christians are troublemakers",
          "It's politically motivated"
        ],
        hint: "It's about fundamental challenge to human systems",
        funFact: "⚔️ Gospel truth confronts comfortable lies - inevitable conflict!"
      }
    },

    76: {
      name: "The Noble Bereans",
      challenge: "They examined the Scriptures daily to see if these things were so...",
      easy: {
        question: "What made the Bereans more noble?",
        options: [
          "Their wealth",
          "They examined the Scriptures daily",
          "Their education",
          "Their Roman citizenship"
        ],
        hint: "They were careful students of God's Word",
        funFact: "📖 Berean = biblical fact-checker - testing teaching against Scripture!"
      },
      medium: {
        question: "How did the Bereans respond to Paul's message?",
        options: [
          "They rejected it immediately",
          "They received it eagerly but tested it against Scripture",
          "They accepted it blindly",
          "They were indifferent"
        ],
        hint: "Eager but careful examination",
        funFact: "🔍 Perfect balance: eager hearts + careful minds = noble faith!"
      },
      hard: {
        question: "What does the Berean example teach us today?",
        options: [
          "Reject all teaching",
          "Test all teaching against Scripture while remaining open",
          "Accept everything from church leaders",
          "Avoid biblical study"
        ],
        hint: "It's about discerning faithfulness to God's Word",
        funFact: "⚖️ Berean principle: Love Scripture more than any human teacher!"
      }
    },

    77: {
      name: "Paul in Athens",
      challenge: "I perceive that in every way you are very religious...",
      easy: {
        question: "Where did Paul debate in Athens?",
        options: [
          "In the temple",
          "In the Areopagus (Mars Hill)",
          "In the stadium",
          "In the palace"
        ],
        hint: "The place of intellectual debate and judgment",
        funFact: "🏛️ Mars Hill = Supreme Court of ideas - perfect platform for Gospel!"
      },
      medium: {
        question: "What altar inscription caught Paul's attention?",
        options: [
          "To the True God",
          "To the Unknown God",
          "To All Gods",
          "To Zeus"
        ],
        hint: "An altar for a god they didn't know",
        funFact: "❓ 'Unknown God' becomes perfect opening to reveal the true God!"
      },
      hard: {
        question: "What does Paul's approach in Athens teach about evangelism?",
        options: [
          "Use only biblical language",
          "Adapt your approach to connect with your audience's understanding",
          "Avoid intellectual discussions",
          "Criticize all other beliefs"
        ],
        hint: "It's about building bridges while maintaining truth",
        funFact: "🌉 Paul quotes Greek poets to Greek philosophers - cultural bridge-building!"
      }
    },

    78: {
      name: "Ministry in Corinth", 
      challenge: "Do not be afraid, but speak and do not be silent...",
      easy: {
        question: "How long did Paul stay in Corinth?",
        options: ["6 months", "1 year", "18 months", "2 years"],
        hint: "A year and a half",
        funFact: "📅 18 months in sinful Corinth - patience with difficult people!"
      },
      medium: {
        question: "What vision encouraged Paul in Corinth?",
        options: [
          "Jesus appeared to him",
          "The Lord said 'Do not be afraid, I have many people in this city'",
          "An angel visited him",
          "He saw heaven opened"
        ],
        hint: "Divine encouragement about future harvest",
        funFact: "👥 God sees future believers before they're saved - hope for any city!"
      },
      hard: {
        question: "What does God's encouragement to Paul teach us?",
        options: [
          "Ministry is always easy",
          "God sustains us in difficult places and gives hope",
          "We should avoid hard places",
          "Success is guaranteed"
        ],
        hint: "It's about divine strength for challenging ministry",
        funFact: "💪 Even apostles need encouragement - God provides strength for hard places!"
      }
    }
  }
};

export default chapter6Translations;